// Admin roles and permissions constants

export const ADMIN_ROLES = [
  "root",
  "admin",
  "branch_manager",
  "rate_manager",
  "staff",
];

export const ACCESS_LEVELS = ["VIEW", "MANAGE", "FULL_CONTROL"];

export const PERMISSION_MODULES = [
  "rates",
  "pricingRules",
  "offers",
  "products",
  "reports",
  "customers",
  "branches",
  "admins",
];

export const RATE_TYPES = [
  "GOLD",
  "SILVER",
  "PLATINUM",
  "DIAMOND",
  "RUBY",
  "EMERALD",
  "SAPPHIRE",
  "PEARL",
];

export const APPROVAL_TYPES = [
  "RATE_UPDATE",
  "PRICING_RULE_CREATE",
  "OFFER_CREATE",
  "HIGH_DISCOUNT",
  "BRANCH_CREATE",
];

export const DEFAULT_PERMISSIONS = {
  viewer: {
    rates: {
      enabled: true,
      permissions: {
        view: true,
        create: false,
        update: false,
        delete: false,
        approve: false,
        schedule: false,
        export: false,
      },
    },
    products: {
      enabled: true,
      permissions: {
        view: true,
        create: false,
        update: false,
        delete: false,
        updatePricing: false,
        manageStock: false,
        bulkUpdate: false,
      },
    },
    reports: {
      enabled: true,
      permissions: {
        viewSales: true,
        viewInventory: false,
        viewFinancial: false,
        viewCustomer: false,
        export: false,
      },
    },
  },

  staff: {
    rates: {
      enabled: true,
      permissions: {
        view: true,
        create: false,
        update: false,
        delete: false,
        approve: false,
        schedule: false,
        export: false,
      },
    },
    products: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: false,
        updatePricing: false,
        manageStock: true,
        bulkUpdate: false,
      },
    },
    customers: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: false,
        approve: false,
        schedule: false,
        export: false,
      },
    },
    reports: {
      enabled: true,
      permissions: {
        viewSales: true,
        viewInventory: true,
        viewFinancial: false,
        viewCustomer: true,
        export: false,
      },
    },
  },

  manager: {
    rates: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: false,
        approve: false,
        schedule: true,
        export: true,
      },
      restrictions: {
        maxChangePercent: 5,
        requiresApproval: true,
        allowedTypes: ["GOLD", "SILVER"],
      },
    },
    pricingRules: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: false,
        approve: false,
        schedule: true,
        export: true,
      },
      restrictions: {
        maxPriority: 10,
        maxMakingChargePercent: 15,
        maxWastagePercent: 5,
        canModifyHallmarkCharges: false,
      },
    },
    offers: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: false,
        approve: false,
        schedule: true,
        export: true,
      },
      restrictions: {
        maxDiscountPercent: 10,
        maxDiscountAmount: 5000,
        requiresApproval: true,
      },
    },
    products: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        updatePricing: true,
        manageStock: true,
        bulkUpdate: true,
      },
    },
    customers: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        approve: false,
        schedule: false,
        export: true,
      },
    },
    reports: {
      enabled: true,
      permissions: {
        viewSales: true,
        viewInventory: true,
        viewFinancial: true,
        viewCustomer: true,
        export: true,
      },
    },
    branches: {
      enabled: true,
      permissions: {
        view: true,
        create: false,
        update: true,
        delete: false,
        approve: false,
        schedule: false,
        export: true,
      },
    },
  },

  admin: {
    rates: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        approve: true,
        schedule: true,
        export: true,
      },
      restrictions: {
        maxChangePercent: 10,
        requiresApproval: false,
      },
    },
    pricingRules: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        approve: true,
        schedule: true,
        export: true,
      },
    },
    offers: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        approve: true,
        schedule: true,
        export: true,
      },
    },
    products: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        updatePricing: true,
        manageStock: true,
        bulkUpdate: true,
      },
    },
    reports: {
      enabled: true,
      permissions: {
        viewSales: true,
        viewInventory: true,
        viewFinancial: true,
        viewCustomer: true,
        export: true,
      },
    },
    customers: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        approve: true,
        schedule: false,
        export: true,
      },
    },
    branches: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        approve: false,
        schedule: false,
        export: true,
      },
    },
    admins: {
      enabled: true,
      permissions: {
        view: true,
        create: true,
        update: true,
        delete: true,
        approve: false,
        schedule: false,
        export: true,
      },
    },
  },

  root: {
    // Root has all permissions
    ALL_ACCESS: true,
  },
};

// Permission action labels
export const PERMISSION_ACTION_LABELS = {
  view: "View",
  create: "Create",
  update: "Update",
  delete: "Delete",
  approve: "Approve",
  schedule: "Schedule",
  export: "Export",
  updatePricing: "Update Pricing",
  manageStock: "Manage Stock",
  bulkUpdate: "Bulk Update",
  viewSales: "View Sales",
  viewInventory: "View Inventory",
  viewFinancial: "View Financial",
  viewCustomer: "View Customer",
};

// Module labels
export const MODULE_LABELS = {
  rates: "Rate Management",
  pricingRules: "Pricing Rules",
  offers: "Offers & Discounts",
  products: "Products",
  reports: "Reports",
  customers: "Customers",
  branches: "Branches",
  admins: "Admin Management",
};

// Approval status colors
export const APPROVAL_STATUS_COLORS = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error",
  EXPIRED: "default",
};

// Role colors for UI
export const ROLE_COLORS = {
  root: "error",
  admin: "primary",
  branch_manager: "secondary",
  rate_manager: "info",
  staff: "default",
};
